// End-to-end encrypted cross-device backup (zero-knowledge).
// All financial data stays private: the server only ever receives AES-GCM
// ciphertext + salt + iv. The encryption key is derived from a PIN that only
// the user knows (PBKDF2), so no one — including admins — can read the data.

const enc = new TextEncoder();
const dec = new TextDecoder();

function b64(buf) {
  const bytes = new Uint8Array(buf);
  let bin = "";
  const chunk = 0x8000;
  for (let i = 0; i < bytes.length; i += chunk) {
    bin += String.fromCharCode.apply(null, bytes.subarray(i, i + chunk));
  }
  return btoa(bin);
}

function unb64(s) {
  return Uint8Array.from(atob(s), (c) => c.charCodeAt(0));
}

async function deriveKey(pin, salt) {
  const base = await crypto.subtle.importKey("raw", enc.encode(pin), "PBKDF2", false, ["deriveKey"]);
  return crypto.subtle.deriveKey(
    { name: "PBKDF2", salt, iterations: 150000, hash: "SHA-256" },
    base,
    { name: "AES-GCM", length: 256 },
    false,
    ["encrypt", "decrypt"]
  );
}

// Gather every CeKKeU localStorage key (all prefixed "fcs").
export function collectLocalData() {
  const out = {};
  for (let i = 0; i < localStorage.length; i++) {
    const k = localStorage.key(i);
    if (k && k.startsWith("fcs")) out[k] = localStorage.getItem(k);
  }
  return out;
}

export function hasLocalData() {
  return Object.keys(collectLocalData()).length > 0;
}

export async function encryptBackup(pin) {
  const data = collectLocalData();
  const salt = crypto.getRandomValues(new Uint8Array(16));
  const iv = crypto.getRandomValues(new Uint8Array(12));
  const key = await deriveKey(pin, salt);
  const ct = await crypto.subtle.encrypt(
    { name: "AES-GCM", iv },
    key,
    enc.encode(JSON.stringify(data))
  );
  return { blob: b64(ct), salt: b64(salt), iv: b64(iv) };
}

export async function decryptBackup({ blob, salt, iv }, pin) {
  const key = await deriveKey(pin, unb64(salt));
  const pt = await crypto.subtle.decrypt(
    { name: "AES-GCM", iv: unb64(iv) },
    key,
    unb64(blob)
  );
  return JSON.parse(dec.decode(pt));
}

export function restoreLocalData(obj) {
  Object.entries(obj || {}).forEach(([k, v]) => {
    if (k.startsWith("fcs") && typeof v === "string") {
      try { localStorage.setItem(k, v); } catch { /* ignore */ }
    }
  });
}
